import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Section } from "@/components/site/Section";
import { CTAButton, mailto } from "@/components/site/CTAButton";

export const Route = createFileRoute("/eu-trademarks")({
  head: () => ({
    meta: [
      { title: "EU Trademark Application Guide for US Brands — Bauer IP" },
      {
        name: "description",
        content:
          "Step-by-step EU trademark application at the EUIPO: filing process, timeline, official fees, common refusals and opposition risk for US brands.",
      },
      {
        property: "og:title",
        content: "EU Trademark Application: Filing Hub for US Brands",
      },
      { property: "og:image", content: "https://bauerip.com/og-us.jpg" },
      { name: "twitter:image", content: "https://bauerip.com/og-us.jpg" },
      {
        property: "og:description",
        content:
          "How to register an EU trade mark: EUIPO process, fees, timeline and the refusals that stop US applications.",
      },
      { property: "og:url", content: "https://bauerip.com/eu-trademarks" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bauerip.com/eu-trademarks" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "European trade mark counsel",
          provider: { "@type": "Person", name: "Karel Bauer" },
          areaServed: "EU",
          audience: {
            "@type": "Audience",
            audienceType: "US brands expanding into Europe",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to file an EU trademark application (EUTM)",
          description:
            "The EUIPO filing process for US brands, from clearance search to registration.",
          totalTime: "P5M",
          estimatedCost: {
            "@type": "MonetaryAmount",
            currency: "EUR",
            value: "1050",
          },
          step: steps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.title,
            text: s.body,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: TrademarksPage,
});

const steps = [
  {
    title: "Clearance search before you commit",
    body: "Search EUTMs, national registers and unregistered rights in your key markets. The EUIPO does not refuse your application over earlier marks — third parties do, through opposition — so conflicts must be found before filing, not after.",
  },
  {
    title: "Choose the route: direct EUTM or Madrid Protocol",
    body: "A direct EUIPO filing gives you full control over the specification. Designating the EU through the Madrid Protocol is convenient if you already hold a US registration and want several territories at once, but it stays dependent on the US base mark for five years.",
  },
  {
    title: "Draft the specification and pick Nice classes",
    body: "EU practice expects narrower wording than a typical US identification. Class headings no longer cover everything in the class, and broad terms such as 'computer software' draw objections. Two to three classes is normal for a US brand entering Europe.",
  },
  {
    title: "File online at the EUIPO",
    body: "The application is filed electronically in one of the EU languages plus a second working language. A US applicant can file, but once an objection or opposition arises an EEA-based representative is mandatory.",
  },
  {
    title: "Examination on absolute grounds",
    body: "The EUIPO checks distinctiveness, descriptiveness and public-policy grounds, usually within one to two months. Objections come as a written refusal notice with a deadline to reply.",
  },
  {
    title: "Publication and the three-month opposition window",
    body: "Once examination clears, the mark publishes. Owners of earlier rights have three months to oppose. Most oppositions settle through a limitation of goods and services or a coexistence agreement.",
  },
  {
    title: "Registration, then use and renewal",
    body: "With no opposition, registration follows roughly four to six months after filing. The EUTM must be put to genuine use in the EU within five years or it becomes vulnerable to cancellation, and it renews every ten years.",
  },
];

const feeRows: [string, string, string][] = [
  ["EUTM application, first class", "€850", "Online filing at the EUIPO"],
  ["Second class", "€50", "Added to the application fee"],
  ["Each further class", "€150", "Per class, from the third onwards"],
  ["Typical US filing (3 classes)", "€1,050", "Official fees only, before counsel"],
  ["Opposition (filing an opposition)", "€320", "Payable by the opponent"],
  ["Renewal, first class (per 10 years)", "€850", "Plus €50 / €150 per further class"],
];

const timeline: [string, string][] = [
  ["Week 0", "Application filed and filing date secured"],
  ["Weeks 1–8", "Examination on absolute grounds; classification queries"],
  ["Month 2–3", "Publication of the application"],
  ["Months 3–6", "Three-month opposition window runs"],
  ["Months 4–6", "Registration issues if unopposed"],
  ["Year 5", "Genuine-use requirement bites; non-use cancellation risk"],
];

const refusals = [
  {
    title: "Descriptive or non-distinctive marks",
    body: "The most common absolute-grounds refusal. A mark that describes the goods, their quality or their purpose in any official EU language fails — including languages you never considered.",
  },
  {
    title: "A meaning you did not check in another EU language",
    body: "A word that is arbitrary in English can be descriptive or offensive in Spanish, Polish or Finnish. The EUIPO examines across all official languages.",
  },
  {
    title: "Vague specifications and class headings",
    body: "Terms like 'computer software' or a bare class heading draw a classification objection and delay the file. Precise wording avoids it.",
  },
  {
    title: "Opposition by an earlier right",
    body: "Not a refusal by the office, but the outcome that most often stops a US filing. A single earlier national right in one member state can defeat the whole unitary application.",
  },
  {
    title: "Bad-faith and geographical-indication conflicts",
    body: "Filings that clash with protected designations, or that follow a distributor relationship in a way that looks opportunistic, attract challenges.",
  },
];

const usDifferences = [
  {
    title: "An EUTM is a single, unitary right",
    body: "It stands or falls as one registration across all 27 member states. That efficiency cuts both ways: one earlier national right in a single country can defeat the entire application, which is why clearance matters more than US applicants usually expect.",
  },
  {
    title: "Europe is first-to-file, with little unregistered protection",
    body: "There is no meaningful equivalent of US common-law rights or intent-to-use practice. Filing before any public launch announcement is the protection; use alone rarely is.",
  },
  {
    title: "The examiner will not cite earlier marks against you",
    body: "The EUIPO examines only absolute grounds — descriptiveness, distinctiveness, public policy. Conflicts with earlier rights are raised by their owners during the three-month opposition window, not by the office.",
  },
  {
    title: "Nice classes are the same 45, the wording is not",
    body: "Your US specification is a starting point only. EU practice expects narrower, more specific terms, and class headings no longer cover everything in the class. Two to three carefully drafted classes beat a copied US identification.",
  },
  {
    title: "Direct EUIPO filing vs Madrid Protocol",
    body: "Designating the EU through Madrid is convenient when you already hold a US registration and want several territories in one filing, but it remains dependent on the US base mark for five years. A direct EUTM filing avoids that dependency and gives full control over the specification.",
  },
  {
    title: "The UK is separate, and you will need an EEA representative",
    body: "An EUTM has not covered the United Kingdom since Brexit, so a UK application is a separate decision. And while a US applicant can file at the EUIPO, an EEA-based representative becomes mandatory as soon as an objection or opposition arises.",
  },
];

const problems = [

  "Your brand may already be blocked in part of Europe.",
  "Your US filing does not protect you in the EU.",
  "Franchise or licensing expansion can expose weak class coverage.",
  "Distributor relationships can create trade mark control issues.",
  "Opposition risk may delay launch.",
];

const services = [
  "EU trade mark filing strategy",
  "EUTM applications",
  "Clearance and risk review",
  "Oppositions and cancellations",
  "Coexistence agreements",
  "Trade mark support for franchise, licensing and distribution structures",
];

const scenarios = [
  {
    title: "US brand launching in the EU",
    body: "Clearance, filing, class coverage and launch risk — handled before marketing commits.",
  },
  {
    title: "Franchisor entering Europe",
    body: "Trade mark protection, territorial structure, licensing support and control provisions for master franchise deals.",
  },
  {
    title: "Brand facing conflict",
    body: "Opposition, coexistence and negotiation strategy when someone challenges — or you need to challenge.",
  },
];

const faqs = [
  {
    q: "Do I need an EU trade mark or national filings?",
    a: "In most cases an EUTM gives you a single registration covering all EU member states. National filings make sense in specific situations — we look at your markets, conflict landscape and budget.",
  },
  {
    q: "Is my US registration enough in Europe?",
    a: "No. Trade mark rights are territorial. A US registration gives you no protection in the EU; you need an EUTM or national filings.",
  },
  {
    q: "What happens if someone opposes my EU filing?",
    a: "You have a window to respond, negotiate a coexistence, narrow your goods and services, or defend on the merits. Early opposition advice often avoids a fight.",
  },
  {
    q: "Should I file before signing a master franchise deal?",
    a: "Yes. Your trade mark is the asset the franchisee licenses. Filing before signing protects your control and your royalty stream.",
  },
  {
    q: "Can trade mark issues delay European launch?",
    a: "They can — through opposition, conflict with a prior right, or class-coverage gaps. Most of these are predictable with a clearance review.",
  },
  {
    q: "How much does an EU trademark application cost?",
    a: "Official EUIPO fees are €850 for the first class, €50 for the second and €150 for each further class — about €1,050 for a typical three-class US filing, before attorney fees.",
  },
  {
    q: "How long does EU trademark registration take?",
    a: "Roughly four to six months from filing to registration when nobody opposes: one to two months of examination, publication, then a three-month opposition window.",
  },
  {
    q: "Does an EU trademark cover the UK?",
    a: "No. Since Brexit an EUTM no longer covers the United Kingdom. A separate UK application is needed if the UK is a target market.",
  },
];


function TrademarksPage() {
  const consultBody =
    "Hello Karel,\n\nI'd like to discuss EU trade mark protection.\n\n— Name:\n— Company:\n— Website:\n— What I'm planning in Europe (launch, franchise, licensing, distribution, opposition):\n";

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* HERO */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 pt-20 md:pt-28 pb-16 md:pb-20">
            <p className="eyebrow mb-6">For US Brands · EUIPO &amp; National</p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-4xl">
              EU Trade Mark Counsel for{" "}
              <span className="italic text-accent-text">US Brands</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground leading-relaxed">
              Protect your brand in Europe before franchise, licensing or
              distribution expansion creates risk.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton variant="accent"
                href={mailto({
                  subject: "EU trade mark consultation",
                  body: consultBody,
                })}
              >
                Book a Consultation
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "Discuss European expansion",
                  body: consultBody,
                })}
              >
                Discuss Your European Expansion
              </CTAButton>
            </div>
          </div>
        </section>

        {/* FOR WHOM */}
        <Section eyebrow="Who this page is for" title="US companies entering Europe.">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
            This page is for US companies entering Europe through direct sales,
            franchising, licensing, distribution or brand partnerships — where
            the brand itself is the asset under expansion.
          </p>
        </Section>

        {/* PROBLEMS */}
        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="What's at stake" title="Business risks, not legal abstractions.">
            <ul className="grid gap-px md:grid-cols-2 bg-border">
              {problems.map((p, i) => (
                <li
                  key={i}
                  className="bg-background p-8 flex gap-5 text-foreground"
                >
                  <span className="font-serif text-accent-text text-xl">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        {/* SERVICES */}
        <Section eyebrow="Services" title="What I do for trade mark clients.">
          <ul className="grid gap-4 md:grid-cols-2">
            {services.map((s) => (
              <li
                key={s}
                className="border-t border-border pt-4 text-foreground"
              >
                <span className="text-accent-text mr-3">—</span>
                {s}
              </li>
            ))}
          </ul>
        </Section>

        {/* HOW TO FILE */}
        <div className="border-t border-border">
          <Section
            eyebrow="EU trademark application"
            title="How to file an EU trade mark: step by step."
            intro="One application at the EUIPO covers all 27 EU member states. This is the sequence a US brand goes through, and where things usually go wrong."
          >
            <ol className="grid gap-px bg-border border border-border">
              {steps.map((s, i) => (
                <li key={s.title} className="bg-background p-8 md:flex gap-8">
                  <span className="font-serif text-accent-text text-2xl shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl text-foreground leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        </div>

        {/* FEES */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Official EUIPO fees"
            title="What an EU trade mark actually costs."
            intro="Official fees only — attorney fees, clearance searches and opposition work are separate."
          >
            <div className="overflow-x-auto border border-border bg-background">
              <table className="w-full text-left border-collapse">
                <caption className="sr-only">
                  Official EUIPO trade mark fees for 2026
                </caption>
                <thead>
                  <tr className="border-b border-border">
                    <th className="p-4 font-serif text-foreground">Item</th>
                    <th className="p-4 font-serif text-foreground">Official fee</th>
                    <th className="p-4 font-serif text-foreground">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {feeRows.map((r) => (
                    <tr key={r[0]} className="border-b border-border last:border-0">
                      <td className="p-4 text-foreground">{r[0]}</td>
                      <td className="p-4 text-accent-text whitespace-nowrap">{r[1]}</td>
                      <td className="p-4 text-muted-foreground">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-muted-foreground max-w-3xl">
              Fees are EUIPO official rates for online filing and can change. A
              US brand filing in three classes with clearance and prosecution
              typically budgets these official fees plus professional fees.
            </p>
          </Section>
        </div>

        {/* TIMELINE */}
        <Section
          eyebrow="Timeline"
          title="From filing to registration."
          intro="Four to six months is normal when no one opposes. An opposition adds a year or more."
        >
          <ol className="border-y border-border divide-y divide-border">
            {timeline.map(([when, what]) => (
              <li key={when} className="grid md:grid-cols-[200px_1fr] gap-4 py-6">
                <span className="eyebrow text-accent-text">{when}</span>
                <span className="text-foreground leading-relaxed">{what}</span>
              </li>
            ))}
          </ol>
        </Section>

        {/* REFUSALS */}
        <div className="border-t border-border bg-secondary/30">
          <Section
            eyebrow="Common refusals"
            title="Why EU trade mark applications fail."
            intro="Most refusals and oppositions are predictable before filing."
          >
            <div className="grid gap-px md:grid-cols-2 bg-border">
              {refusals.map((r) => (
                <div key={r.title} className="bg-background p-8">
                  <h3 className="font-serif text-xl text-foreground leading-snug">
                    {r.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {r.body}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* US DIFFERENCES */}
        <Section
          eyebrow="Coming from the USPTO"
          title="What changes when a US brand files in Europe."
          intro="Six differences that decide whether an EU trade mark application succeeds or stalls."
        >
          <div className="grid gap-px md:grid-cols-2 bg-border">
            {usDifferences.map((d) => (
              <div key={d.title} className="bg-background p-8">
                <h3 className="font-serif text-xl text-foreground leading-snug">
                  {d.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* RELATED */}
        <Section eyebrow="Go deeper" title="Related guides.">

          <ul className="grid gap-4 md:grid-cols-2">
            <li className="border-t border-border pt-4">
              <Link
                to="/blog/$slug"
                params={{ slug: "eu-trademark-search-registration-us-guide" }}
                className="text-foreground hover:text-accent-text transition-colors"
              >
                <span className="text-accent-text mr-3">—</span>
                EU trademark search and registration for US companies
              </Link>
            </li>
            <li className="border-t border-border pt-4">
              <Link
                to="/blog/$slug"
                params={{ slug: "eu-trademark-application-us-guide" }}
                className="text-foreground hover:text-accent-text transition-colors"
              >
                <span className="text-accent-text mr-3">—</span>
                Does Your US Brand Need an EU Trade Mark?
              </Link>
            </li>
            <li className="border-t border-border pt-4">
              <Link
                to="/blog/$slug"
                params={{ slug: "eu-trademark-class-selection-guide" }}
                className="text-foreground hover:text-accent-text transition-colors"
              >
                <span className="text-accent-text mr-3">—</span>
                EU Trademark Classes (NICE): choosing the right ones
              </Link>
            </li>
            <li className="border-t border-border pt-4">
              <Link
                to="/blog/$slug"
                params={{ slug: "us-trademark-europe-expansion" }}
                className="text-foreground hover:text-accent-text transition-colors"
              >
                <span className="text-accent-text mr-3">—</span>
                US trade marks and European expansion
              </Link>
            </li>
            <li className="border-t border-border pt-4">
              <Link
                to="/services/european-trademark-attorney"
                className="text-foreground hover:text-accent-text transition-colors"
              >
                <span className="text-accent-text mr-3">—</span>
                European trade mark attorney: filing, oppositions, licensing
              </Link>
            </li>
          </ul>
        </Section>


        {/* WHY ME */}
        <div className="border-y border-border bg-primary text-primary-foreground">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24 grid gap-12 md:grid-cols-[1fr_1.4fr]">
            <div>
              <p className="eyebrow mb-4" style={{ color: "var(--accent)" }}>
                Why work with me
              </p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                Direct, partner-level work — in English.
              </h2>
            </div>
            <div className="space-y-6 text-primary-foreground/90 leading-relaxed text-lg">
              <p>
                I am a European patent attorney and trade mark counsel with my
                own established practice. I work directly in English, advise
                foreign clients on European protection strategy, and combine
                legal precision with commercial awareness.
              </p>
              <ul className="grid gap-3 text-base">
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Direct
                  partner-level work
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Fast and
                  practical advice in English
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> European filing
                  and dispute experience
                </li>
                <li className="border-t border-primary-foreground/15 pt-3">
                  <span className="text-accent mr-3">—</span> Useful for
                  expansion-stage businesses, not only large corporations
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* HOW I HELP */}
        <Section eyebrow="How I typically help" title="Three situations I see most often.">
          <div className="grid gap-px md:grid-cols-3 bg-border">
            {scenarios.map((s, i) => (
              <div key={s.title} className="bg-background p-8">
                <p className="font-serif text-accent-text text-2xl">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-4 font-serif text-xl text-foreground leading-snug">
                  {s.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* FAQ */}
        <div className="border-t border-border bg-secondary/30">
          <Section eyebrow="FAQ" title="Common questions.">
            <dl className="divide-y divide-border border-y border-border">
              {faqs.map((f) => (
                <div
                  key={f.q}
                  className="grid md:grid-cols-[1fr_2fr] gap-6 py-8"
                >
                  <dt className="font-serif text-lg text-foreground">{f.q}</dt>
                  <dd className="text-muted-foreground leading-relaxed">
                    {f.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
            <h2 className="font-serif text-3xl md:text-5xl text-foreground leading-tight max-w-3xl">
              Expanding your brand into Europe?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Let&rsquo;s discuss the trade mark issues before they become
              launch problems. When you write, please include your company,
              website, and what you&rsquo;re planning in Europe.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <CTAButton variant="accent"
                href={mailto({
                  subject: "Review my European brand expansion risk",
                  body: consultBody,
                })}
              >
                Review Your European Brand Expansion Risk
              </CTAButton>
              <CTAButton
                variant="secondary"
                href={mailto({
                  subject: "EU trade mark strategy call",
                  body: consultBody,
                })}
              >
                Discuss Your EU Trade Mark Strategy
              </CTAButton>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
